/// <amd-module name="Dashboards/WidgetContractServices/SemanticVersionService" />

import { SemanticVersion } from "Dashboards/Common/Contracts/Dashboard";
import { format } from "VSS/Core/Util/String";
import { IVssService, Services, VssService } from "VSS/Platform/Context";

/** Provides helpers for working with widget semantic versions. */
export interface ISemanticVersionService extends IVssService {
    /**
     * Verify if a semantic version is valid or not (whether each of major, minor and patch are positive non floating point numbers)
     * @param version the semantic version to test.
     */
    verifyVersionValid(version: SemanticVersion): boolean;

    /**
     * Verify if the new version is ordinally the same or after the old version (i.e. newVersion >= oldVersion).
     * @param oldVersion the old semantic version to test against.
     * @param newVersion the new semantic version to test for.
     */
    verifyVersionForward(oldVersion: SemanticVersion, newVersion: SemanticVersion): boolean;

    /**
     * Verifies two versions are exactly the same.
     * @param oldVersion the old semantic version to test against.
     * @param newVersion the new semantic version to test for.
     */
    verifyVersionsEqual(oldVersion: SemanticVersion, newVersion: SemanticVersion): boolean;

    /**
     *  convert version to string.
     *  @param version the version to convert in form.
     */
    versionToString(version: SemanticVersion): string;

    /**
     * Verifies whether a version is initial.
     * @param: version to be verified.
     */
    isInitialVersion(version: SemanticVersion): boolean;

    /**
     * Return the initial version that all settings get: 1.0.0
     */
    getInitialVersion(): SemanticVersion;
}

export class SemanticVersionService extends VssService {
    public verifyVersionValid(version: SemanticVersion): boolean {
        return (
            !!version &&
            /^(0|[1-9]\d*)$/.test(version.major.toString()) &&
            /^(0|[1-9]\d*)$/.test(version.minor.toString()) &&
            /^(0|[1-9]\d*)$/.test(version.patch.toString())
        );
    }

    public verifyVersionForward(oldVersion: SemanticVersion, newVersion: SemanticVersion): boolean {
        return (
            newVersion.major > oldVersion.major ||
            (newVersion.major === oldVersion.major && newVersion.minor > oldVersion.minor) ||
            (newVersion.major === oldVersion.major && newVersion.minor === oldVersion.minor && newVersion.patch >= oldVersion.patch)
        );
    }

    public verifyVersionsEqual(oldVersion: SemanticVersion, newVersion: SemanticVersion): boolean {
        return newVersion.major === oldVersion.major && newVersion.minor === oldVersion.minor && newVersion.patch === oldVersion.patch;
    }

    public versionToString(version: SemanticVersion): string {
        return format("{0}.{1}.{2}", version.major, version.minor, version.patch);
    }

    public isInitialVersion(version: SemanticVersion): boolean {
        return this.verifyVersionsEqual(this.getInitialVersion(), version);
    }

    public getInitialVersion(): SemanticVersion {
        return {
            major: 1,
            minor: 0,
            patch: 0
        };
    }
}

Services.add("ISemanticVersionService", { serviceFactory: SemanticVersionService });
